import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { useAuth } from '../context/AuthContext';

export const MagicLinkCallback: React.FC = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { consumeMagicLink } = useAuth();
	const [status, setStatus] = useState<'ready' | 'processing' | 'success' | 'error'>('ready');
	const [error, setError] = useState<string | null>(null);
	const hasProcessed = useRef(false);
	const [token, setToken] = useState<string | null>(null);

	useEffect(() => {
		if (hasProcessed.current) {
			return;
		}
		hasProcessed.current = true;

		const tokenValue = searchParams.get('token');
		if (!tokenValue) {
			setError('Missing magic link token');
			setStatus('error');
			return;
		}
		setToken(tokenValue);
	}, [searchParams, consumeMagicLink, navigate]);

	const handleConfirm = async () => {
		/* istanbul ignore next -- token is always set before Continue button renders; guard is defensive only */
		if (!token) {
			setError('Missing magic link token');
			setStatus('error');
			return;
		}

		try {
			setStatus('processing');
			await consumeMagicLink(token);
			setStatus('success');
			setTimeout(() => {
				navigate('/');
			}, 1500);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to consume magic link');
			setStatus('error');
		}
	};

	if (status === 'processing') {
		return (
			<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
				<CircularProgress sx={{ mb: 2 }} />
				<Typography variant="body1">Signing you in...</Typography>
			</Box>
		);
	}

	if (status === 'error') {
		return (
			<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" p={3}>
				<Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
					{error || 'Failed to sign in'}
				</Alert>
				<Typography variant="body2" color="text.secondary">
					Please request a new magic link and try again.
				</Typography>
			</Box>
		);
	}

	if (status === 'ready') {
		return (
			<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" p={3}>
				<Alert severity="info" sx={{ mb: 2, maxWidth: 600 }}>
					Click continue to finish signing in.
				</Alert>
				<Button variant="contained" onClick={handleConfirm}>
					Continue
				</Button>
			</Box>
		);
	}

	return (
		<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
			<Alert severity="success" sx={{ mb: 2, maxWidth: 600 }}>
				Sign-in successful!
			</Alert>
			<Typography variant="body2" color="text.secondary">
				Redirecting to the application...
			</Typography>
		</Box>
	);
};
